// src/directives/loading.js
import Vue from 'vue'
import { Loading } from 'vant'

// 默认配置常量
const DEFAULT_CONFIG = {
  color: '#c9c9c9',
  type: 'spinner',
  size: '30px',
  textSize: '14px',
  textColor: '#c9c9c9',
  vertical: false,
  text: ''
}

// 样式常量
const WRAPPER_STYLE = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 999
}

/**
 * 将字符串转换为布尔值
 * @param {String|Boolean} value - 要转换的值
 * @param {Boolean} defaultValue - 默认值
 * @returns {Boolean}
 */
function toBoolean(value, defaultValue = false) {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value !== 'false' && value !== '0' && value !== ''
  }
  return defaultValue
}

/**
 * 从元素获取属性值
 * @param {HTMLElement} el - 元素
 * @param {String} name - 属性名
 * @param {*} defaultValue - 默认值
 * @returns {*}
 */
function getAttr(el, name, defaultValue = '') {
  const value = el.getAttribute(name)
  return value !== null ? value : defaultValue
}

// 创建一个包装组件来使用 Vant Loading
const LoadingWrapper = {
  name: 'LoadingWrapper',
  components: {
    VanLoading: Loading
  },
  props: {
    color: String,
    type: String,
    size: [String, Number],
    textSize: [String, Number],
    textColor: String,
    vertical: {
      type: Boolean,
      default: true
    },
    text: String,
    message: String,
    value: {
      type: Boolean,
      default: false
    }
  },
  render(h) {
    if (!this.value) {
      return null
    }

    // 获取文案，支持 text 和 message 两种属性名
    const loadingText = this.text || this.message || ''

    return h('div', {
      class: 'v-loading-wrapper',
      style: WRAPPER_STYLE
    }, [
      h('van-loading', {
        props: {
          color: this.color,
          type: this.type,
          size: this.size,
          textSize: this.textSize,
          textColor: this.textColor,
          vertical: this.vertical
        }
      }, loadingText ? [loadingText] : [])
    ])
  }
}

// 创建 Loading 组件构造函数
const LoadingConstructor = Vue.extend(LoadingWrapper)

/**
 * 规范化配置对象，处理各种属性名的兼容性
 * @param {Object} config - 配置对象
 * @returns {Object} 规范化后的配置
 */
function normalizeConfig(config) {
  const normalized = { ...config }

  // 处理 kebab-case 到 camelCase 的转换
  const mappings = {
    'text-size': 'textSize',
    'text-color': 'textColor',
    'loading-text': 'text'
  }

  Object.keys(mappings).forEach(kebabKey => {
    if (normalized[kebabKey] !== undefined) {
      normalized[mappings[kebabKey]] = normalized[kebabKey]
      delete normalized[kebabKey]
    }
  })

  // 统一文案处理：message 和 loading-text 都转换为 text
  if (normalized.message !== undefined && !normalized.text) {
    normalized.text = normalized.message
  }

  // 处理 vertical 布尔值
  if (normalized.vertical !== undefined) {
    normalized.vertical = toBoolean(normalized.vertical, DEFAULT_CONFIG.vertical)
  }

  return normalized
}

/**
 * 获取 loading 配置选项
 * @param {HTMLElement} el - 指令绑定的元素
 * @param {*} value - 指令的值
 * @param {Object} modifiers - 指令修饰符
 * @returns {Object} 配置对象
 */
function getLoadingOptions(el, value, modifiers) {
  // 从元素属性获取配置
  const options = {
    color: getAttr(el, 'loading-color', DEFAULT_CONFIG.color),
    type: getAttr(el, 'loading-type', DEFAULT_CONFIG.type),
    size: getAttr(el, 'loading-size', DEFAULT_CONFIG.size),
    textSize: getAttr(el, 'loading-text-size', DEFAULT_CONFIG.textSize),
    textColor: getAttr(el, 'loading-text-color', DEFAULT_CONFIG.textColor),
    vertical: toBoolean(getAttr(el, 'loading-vertical'), DEFAULT_CONFIG.vertical),
    text: getAttr(el, 'loading-text', DEFAULT_CONFIG.text),
    value: false // 初始状态
  }

  // 如果是指令值是对象，则合并配置
  if (typeof value === 'object' && value !== null) {
    const normalizedValue = normalizeConfig(value)
    Object.assign(options, normalizedValue)
  }

  return options
}

/**
 * 创建并挂载 loading 实例
 * @param {HTMLElement} el - 指令绑定的元素
 * @param {Object} options - loading 配置选项
 * @param {Object} modifiers - 指令修饰符
 * @returns {VueComponent} loading 实例
 */
function createLoadingInstance(el, options, modifiers) {
  if (!LoadingConstructor) {
    console.error('[v-loading] LoadingConstructor is not available')
    return null
  }

  try {
    // 创建 loading 实例
    const loadingInstance = new LoadingConstructor({
      propsData: {
        ...options,
        value: true
      }
    })

    // 挂载到临时容器
    loadingInstance.$mount()

    // 确保实例已正确创建
    if (!loadingInstance || !loadingInstance.$el) {
      console.error('[v-loading] Loading instance or $el is not available')
      return null
    }

    // 设置父元素相对定位
    if (!modifiers.fullscreen) {
      // 设置父元素相对定位（仅在需要时）
      setParentPosition(el)
      el.appendChild(loadingInstance.$el)

      // 给兄弟元素添加模糊效果
      applyBlurToSiblings(el, loadingInstance.$el)

      // 禁用父元素滚动
      disableParentScroll(el)
    } else {
      // 全屏加载
      document.body.appendChild(loadingInstance.$el)
      loadingInstance.$el.style.position = 'fixed'
      loadingInstance.$el.style.top = '0'
      loadingInstance.$el.style.left = '0'
      loadingInstance.$el.style.width = '100%'
      loadingInstance.$el.style.height = '100%'
      loadingInstance.$el.style.zIndex = '9999'

      // 全屏模式下禁用 body 滚动
      disableParentScroll(document.body)
    }

    return loadingInstance
  } catch (error) {
    console.error('[v-loading] Failed to create loading instance:', error)
    return null
  }
}

/**
 * 给兄弟元素添加模糊效果
 * @param {HTMLElement} el - 指令绑定的元素
 * @param {HTMLElement} loadingEl - loading 元素
 */
function applyBlurToSiblings(el, loadingEl) {
  if (!el || !loadingEl) return

  const children = Array.from(el.children)
  const originalFilters = []

  children.forEach((child, index) => {
    // 跳过 loading 元素本身
    if (child === loadingEl || child.contains(loadingEl)) {
      originalFilters.push(null)
      return
    }

    // 保存原始 filter 值
    const originalFilter = child.style.filter || ''
    originalFilters.push(originalFilter)

    // 应用模糊效果
    child.style.filter = originalFilter
      ? `${originalFilter} blur(3px)`
      : 'blur(3px)'
  })

  // 保存原始 filter 值到元素上，以便后续恢复
  el._loadingOriginalFilters = originalFilters
}

/**
 * 移除兄弟元素的模糊效果
 * @param {HTMLElement} el - 指令绑定的元素
 */
function removeBlurFromSiblings(el) {
  if (!el || !el._loadingOriginalFilters) return

  const children = Array.from(el.children)
  const originalFilters = el._loadingOriginalFilters

  children.forEach((child, index) => {
    if (index < originalFilters.length && originalFilters[index] !== null) {
      // 恢复原始 filter 值
      if (originalFilters[index]) {
        child.style.filter = originalFilters[index]
      } else {
        child.style.filter = ''
      }
    }
  })

  // 清理保存的数据
  delete el._loadingOriginalFilters
}

/**
 * 设置父元素相对定位（仅在需要时）
 * @param {HTMLElement} el - 指令绑定的元素
 */
function setParentPosition(el) {
  if (!el) return

  // 获取计算后的 position 值
  const computedStyle = window.getComputedStyle(el)
  const currentPosition = computedStyle.position

  // 保存原始的 position 值（从 style 属性获取，如果没有则保存计算值）
  if (!el._loadingOriginalPosition) {
    el._loadingOriginalPosition = el.style.position || currentPosition
  }

  // 如果当前是 static 或没有设置 position，设置为 relative
  if (currentPosition === 'static' || !el.style.position) {
    el.style.position = 'relative'
  }
}

/**
 * 恢复父元素原始定位
 * @param {HTMLElement} el - 指令绑定的元素
 */
function restoreParentPosition(el) {
  if (!el || !el._loadingOriginalPosition) return

  // 恢复原始的 position 值
  if (el._loadingOriginalPosition && el._loadingOriginalPosition !== 'static') {
    el.style.position = el._loadingOriginalPosition
  } else {
    el.style.position = ''
  }

  // 清理保存的数据
  delete el._loadingOriginalPosition
}

/**
 * 禁用父元素滚动
 * @param {HTMLElement} el - 指令绑定的元素
 */
function disableParentScroll(el) {
  if (!el) return

  // 保存原始的 overflow 值
  const originalOverflow = el.style.overflow || ''
  el._loadingOriginalOverflow = originalOverflow

  // 设置 overflow: hidden
  el.style.overflow = 'hidden'
}

/**
 * 启用父元素滚动
 * @param {HTMLElement} el - 指令绑定的元素
 */
function enableParentScroll(el) {
  if (!el) return

  // 恢复原始的 overflow 值
  if (el._loadingOriginalOverflow) {
    el.style.overflow = el._loadingOriginalOverflow
  } else {
    el.style.overflow = ''
  }

  // 清理保存的数据
  delete el._loadingOriginalOverflow
}

/**
 * 销毁 loading 实例
 * @param {VueComponent} instance - loading 实例
 */
function destroyInstance(instance) {
  if (!instance) return

  try {
    instance.$destroy()
    if (instance.$el && instance.$el.parentNode) {
      instance.$el.parentNode.removeChild(instance.$el)
    }
  } catch (error) {
    console.warn('[v-loading] Error destroying instance:', error)
  }
}

/**
 * 判断是否需要显示 loading
 * @param {*} value - 指令的值
 * @returns {Boolean}
 */
function shouldShowLoading(value) {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'object' && value !== null) {
    return value.loading !== undefined ? value.loading : true
  }
  return !!value
}

const loadingDirective = {
  inserted(el, binding) {
    const { value, modifiers } = binding

    // 获取配置
    const options = getLoadingOptions(el, value, modifiers)

    // 存储配置到元素
    el.loadingOptions = options
    el.loadingModifiers = modifiers

    // 只有在需要显示时才创建实例
    if (shouldShowLoading(value)) {
      const loadingInstance = createLoadingInstance(el, options, modifiers)
      if (loadingInstance) {
        el.instance = loadingInstance
      }
    }
  },

  update(el, binding) {
    const { value } = binding
    const instance = el.instance
    const shouldShow = shouldShowLoading(value)

    // 如果当前需要显示但没有实例，创建实例
    if (shouldShow && !instance) {
      const options = el.loadingOptions || getLoadingOptions(el, value, binding.modifiers)
      const modifiers = el.loadingModifiers || binding.modifiers
      const loadingInstance = createLoadingInstance(el, options, modifiers)
      if (loadingInstance) {
        el.instance = loadingInstance
      }
      return
    }

    // 如果不需要显示但有实例，销毁实例
    if (!shouldShow && instance) {
      // 移除兄弟元素的模糊效果
      if (!binding.modifiers.fullscreen) {
        removeBlurFromSiblings(el)
        // 恢复父元素原始定位
        restoreParentPosition(el)
        // 启用父元素滚动
        enableParentScroll(el)
      } else {
        // 全屏模式下恢复 body 滚动
        enableParentScroll(document.body)
      }

      destroyInstance(instance)
      el.instance = null
      return
    }

    // 如果实例存在，更新状态和配置
    if (instance) {
      const oldValue = instance.value

      // 如果值是一个对象，更新配置
      if (typeof value === 'object' && value !== null) {
        const normalizedValue = normalizeConfig(value)
        // 更新配置属性
        Object.keys(normalizedValue).forEach(key => {
          if (key !== 'loading' && key !== 'value' && instance[key] !== undefined) {
            // 特殊处理 vertical 布尔值
            if (key === 'vertical') {
              instance[key] = toBoolean(normalizedValue[key], instance[key])
            } else {
              instance[key] = normalizedValue[key]
            }
          }
        })
      }

      // 更新 loading 状态
      instance.value = shouldShow

      // 根据 loading 状态变化，应用或移除样式
      if (shouldShow && !oldValue) {
        // 从 false 变为 true，应用样式
        if (!binding.modifiers.fullscreen) {
          setParentPosition(el)
          applyBlurToSiblings(el, instance.$el)
          disableParentScroll(el)
        } else {
          disableParentScroll(document.body)
        }
      } else if (!shouldShow && oldValue) {
        // 从 true 变为 false，移除样式
        if (!binding.modifiers.fullscreen) {
          removeBlurFromSiblings(el)
          restoreParentPosition(el)
          enableParentScroll(el)
        } else {
          enableParentScroll(document.body)
        }
      }
    }
  },

  unbind(el) {
    // 清理实例
    if (el.instance) {
      // 移除兄弟元素的模糊效果
      removeBlurFromSiblings(el)
      // 恢复父元素原始定位
      restoreParentPosition(el)
      // 启用父元素滚动
      enableParentScroll(el)
      // 如果全屏模式，恢复 body 滚动
      enableParentScroll(document.body)

      destroyInstance(el.instance)
      el.instance = null
    }
    // 清理存储的数据
    delete el.loadingOptions
    delete el.loadingModifiers
  }
}

export default loadingDirective
